import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Priority } from "@zero/types/enum";
import type { TodoItem } from "@zero/types/todo";
import { cn } from "@zero/utils";
import { AlertCircle, AlignLeft, CalendarDays } from "lucide-react";
import { type CSSProperties, memo } from "react";
import { formatDueDate, isOverdue } from "../../utils/date";
import { isTodoCompleted } from "../../utils/todoRights";

type Props = {
  todo: TodoItem;
  disabled?: boolean;
  isDragging?: boolean;
  onOpen?: (id: string) => void;
};

function KanbanCard({ todo, disabled = false, isDragging, onOpen }: Props) {
  const completed = isTodoCompleted(todo);
  const dragDisabled = disabled || completed || todo.relationship === "viewer";
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: todo.id,
    disabled: dragDisabled,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getPriorityColor = (priority?: Priority) => {
    switch (priority) {
      case Priority.Urgent:
        return "text-red-600";
      case Priority.High:
        return "text-orange-500";
      case Priority.Medium:
        return "text-yellow-500";
      case Priority.Low:
        return "text-blue-500";
      default:
        return "text-gray-400";
    }
  };

  const getPriorityBg = (priority?: Priority) => {
    switch (priority) {
      case Priority.Urgent:
        return "bg-red-600/10 dark:bg-red-600/20";
      case Priority.High:
        return "bg-orange-500/10 dark:bg-orange-500/20";
      case Priority.Medium:
        return "bg-yellow-500/10 dark:bg-yellow-500/20";
      case Priority.Low:
        return "bg-blue-500/10 dark:bg-blue-500/20";
      default:
        return "bg-gray-500/10 dark:bg-gray-500/20";
    }
  };
  const overdue = isOverdue(todo.dueDate, completed);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`
                p-4 rounded-lg border cursor-grab active:cursor-grabbing
                bg-card hover:shadow-md transition-shadow
                ${isDragging ? "opacity-50" : ""}
                ${dragDisabled ? "opacity-70" : ""}
            `}
    >
      <div className="space-y-3">
        {/* 标题 */}
        <button
          type="button"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            onOpen?.(todo.id);
          }}
          className="line-clamp-2 text-left text-sm font-medium text-foreground hover:text-primary"
        >
          {todo.title}
        </button>

        {/* 描述 */}
        {todo.description && (
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <AlignLeft size={12} className="mt-0.5 flex-shrink-0" />
            <p className="line-clamp-2">{todo.description}</p>
          </div>
        )}

        {/* 底部信息 */}
        <div className="flex items-center justify-between">
          {/* 优先级 */}
          {todo.priority && (
            <div
              className={`
                                flex items-center gap-1 px-2 py-1 rounded text-xs font-medium
                                ${getPriorityBg(todo.priority)} ${getPriorityColor(todo.priority)}
                            `}
            >
              <AlertCircle size={12} />
              <span>
                {todo.priority === Priority.Urgent
                  ? "紧急"
                  : todo.priority === Priority.High
                    ? "高"
                    : todo.priority === Priority.Medium
                      ? "中"
                      : "低"}
              </span>
            </div>
          )}
        </div>

        {todo.dueDate && (
          <div
            className={cn(
              "inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium",
              overdue ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground",
            )}
          >
            <CalendarDays size={12} />
            <span>{formatDueDate(todo.dueDate)}</span>
          </div>
        )}

        {/* 分类标签 */}
        {todo.category && (
          <div className="flex items-center gap-2">
            <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">{todo.category}</span>
            {todo.assigneeId && (
              <span className="rounded-full bg-blue-500/10 px-2 py-1 text-xs text-blue-600">指派</span>
            )}
            {todo.relationship !== "viewer" && (
              <span className="rounded-full bg-secondary px-2 py-1 text-xs text-secondary-foreground">
                {todo.relationship === "owner" ? "Mine" : "Assigned"}
              </span>
            )}
            {todo.tags.slice(0, 2).map((tag) => (
              <span key={tag} className="rounded-full bg-secondary px-2 py-1 text-xs text-secondary-foreground">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(KanbanCard);
